import React from 'react';
import { Button } from '@parsina/framework';

import { C } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// The mobile surface's LEAF primitives — the three shapes every mobile screen in
// this app repeats. They are compositions over kit `Button` and tokens, they
// carry no student vocabulary, and they are deliberately app-local: DESIGN.md
// rule 4 says an app-specific component stays in its app until a SECOND app needs
// it.
//
// They exist so that a mobile screen file contains composition and wiring, not
// bespoke widget markup.

/** A section heading over a mobile block. An <h2>, so the screen has landmarks. */
export const MobileSectionTitle = ({ children, count, action, style }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      margin: '0 0 0.5rem',
      ...style,
    }}
  >
    <h2 style={{ margin: 0, fontSize: '0.8125rem', fontWeight: 800, color: C.textPrimary }}>
      {children}
    </h2>
    {count != null && (
      <span style={{ fontSize: '0.6875rem', fontWeight: 700, color: C.textMuted }}>{count}</span>
    )}
    {action && <span style={{ marginInlineStart: 'auto' }}>{action}</span>}
  </div>
);

/**
 * The «نمایش بیشتر» expander.
 *
 * The list grows through an explicit expander, never infinite scroll: a student
 * who scrolled to a case and tapped into it has to come back to the same place,
 * and an auto-loading list has no place to come back to. The desktop `Pager` is a
 * row of 34px buttons, which is under the 44px touch floor.
 */
export const ShowMore = ({ onClick, testId }) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="outline"
      onClick={onClick}
      data-testid={testId}
      className="w-full min-h-11"
      style={{ marginTop: '0.5rem' }}
    >
      {t('mobile.showMore')}
    </Button>
  );
};

/**
 * One label/value line.
 *
 * The desktop record spends its width on a grid; the phone gives every fact its
 * own full-width row, because a long value must never be the thing that forces a
 * 320px viewport into horizontal scroll.
 *
 * `ltr` wraps the value in an LTR isolate for a digit run — a phone number, a
 * gift code, a lab value with its unit.
 */
export const MobileInfoRow = ({ label, value, ltr }) => {
  const { t } = useTranslation();
  const empty = value == null || value === '';
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: '0.75rem',
        padding: '0.625rem 0',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      <span style={{ fontSize: '0.75rem', color: C.textMuted, flexShrink: 0 }}>{label}</span>
      <span
        {...(ltr && !empty ? { dir: 'ltr' } : {})}
        style={{
          fontSize: '0.8125rem',
          color: empty ? C.textMuted : C.textPrimary,
          marginInlineStart: 'auto',
          textAlign: 'end',
          minWidth: 0,
          overflowWrap: 'anywhere',
          ...(ltr && !empty ? { unicodeBidi: 'isolate' } : {}),
        }}
      >
        {empty ? t('common.labels.none') : value}
      </span>
    </div>
  );
};

export default MobileSectionTitle;
